import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ArrowRightOutlined } from "@ant-design/icons";

import { MainColor, SubColor } from "../../style/styleValue";
import SectionHeading from "./SectionHeading";
import useReveal, { revealStyle } from "./useReveal";

interface Member {
  name: string;
  affiliation: string;
}

interface MemberGroup {
  key: string;
  label: string;
  english: string;
  members: Member[];
}

// Bepath 소개 > 운영진 및 자문위원 (명단은 Committee 페이지와 같게 유지)
const advisoryBoard: Member[] = [
  { name: '김정선', affiliation: '삼성서울병원' },
  { name: '김나래', affiliation: '가톨릭대학교부천성모병원' },
  { name: '김은나', affiliation: '서울대학교병원' },
  { name: '전이경', affiliation: '고려대학교구로병원' },
  { name: '김현희', affiliation: '삼성서울병원' },
  { name: '황윤정', affiliation: '분당 서울대학교병원' },
  { name: '김종재', affiliation: '서울아산병원' },
  { name: '김연미', affiliation: '플러스랩병리과의원' },
  { name: '박성혜', affiliation: '씨젠의료재단' },
  { name: '박진우', affiliation: '국립암센터' },
  { name: '우창곡', affiliation: '충북대학교병원' },
  { name: '김성익', affiliation: '국립암센터' },
  { name: '정욱재', affiliation: '창원경상국립대학교병원' },
  { name: '김세훈', affiliation: '연세대학교 세브란스병원' },
  { name: '공준석', affiliation: '원자력병원' },
  { name: '한강희', affiliation: '제주한라병원' },
  { name: '원재경', affiliation: '서울대학교병원' },
  { name: '김한성', affiliation: '인제대학교 일산백병원' },
  { name: '김보미', affiliation: '해운대백병원' },
];

const advisoryBoard2: Member[] = [
  { name: '정의석', affiliation: '서울아산병원' },
  { name: '이주영', affiliation: '고대안암병원' },
];

const snuhTeam: Member[] = [
  { name: '김은나', affiliation: '서울대학교병원' },
  { name: '심유미', affiliation: '서울대학교병원' },
  { name: '노윤서', affiliation: '서울대학교병원' },
  { name: '송현범', affiliation: '서울대학교병원' },
];

const developer: Member[] = [
  { name: '김은성', affiliation: '(주)휴민텍' },
  { name: '김재민', affiliation: '(주)휴민텍' },
  { name: '김혁', affiliation: '(주)휴민텍' },
  { name: '윤나혜', affiliation: '(주)휴민텍' },
];

const sortByName = (members: Member[]) => [...members].sort((a, b) => a.name.localeCompare(b.name, 'ko'));

const advisoryGroups: MemberGroup[] = [
  { key: 'pathology', label: '병리과', english: 'Pathology', members: sortByName(advisoryBoard) },
  { key: 'pediatrics', label: '소아임상', english: 'Pediatrics', members: sortByName(advisoryBoard2) },
];

const teamGroups: MemberGroup[] = [
  { key: 'snuh', label: '서울대학교병원 소아아틀라스 운영진', english: 'Operating Team', members: sortByName(snuhTeam) },
  { key: 'developer', label: '사이트 제작', english: 'Development', members: sortByName(developer) },
];

const advisors = [...advisoryBoard, ...advisoryBoard2];
const advisorCount = advisors.length;
const institutionCount = new Set(advisors.map((member) => member.affiliation)).size;
const teamCount = snuhTeam.length + developer.length;

const CommitteeSection = () => {
  const ref = useReveal<HTMLElement>();
  const navigate = useNavigate();

  return (
    <Wrapper ref={ref} id="committee">
      <div className="container">
        <SectionHeading
          eyebrow="Committee"
          title="소아희귀질환병리아틀라스와 함께하는 분들"
          description="전국 소아병리·소아임상 전문가들이 증례를 검증하고 자문하며 아틀라스의 품질을 함께 지켜갑니다."
        />

        <Summary className="reveal">
          <div>
            <strong>{advisorCount}</strong>
            <span>자문위원</span>
          </div>
          <div>
            <strong>{institutionCount}</strong>
            <span>참여 기관</span>
          </div>
          <div>
            <strong>{teamCount}</strong>
            <span>운영 · 제작</span>
          </div>
        </Summary>

        {advisoryGroups.map((group) => (
          <Group key={group.key} className="reveal">
            <GroupTitle>
              <h3>{group.label}</h3>
              <span>{group.english}</span>
              <em>{group.members.length}명</em>
            </GroupTitle>
            <MemberGrid>
              {group.members.map((member) => (
                <MemberCard key={`${group.key}-${member.name}`}>
                  <div className="avatar">{member.name.charAt(0)}</div>
                  <div className="info">
                    <strong>{member.name}</strong>
                    <span>{member.affiliation}</span>
                  </div>
                </MemberCard>
              ))}
            </MemberGrid>
          </Group>
        ))}

        <TeamRow>
          {teamGroups.map((group, index) => (
            <TeamCard
              key={group.key}
              className="reveal"
              style={{ "--reveal-delay": `${0.12 * index}s` } as React.CSSProperties}
            >
              <span className="english">{group.english}</span>
              <h3>{group.label}</h3>
              <ul>
                {group.members.map((member) => (
                  <li key={`${group.key}-${member.name}`}>
                    <strong>{member.name}</strong>
                    <span>{member.affiliation}</span>
                  </li>
                ))}
              </ul>
            </TeamCard>
          ))}
        </TeamRow>

        <MoreLink className="reveal" type="button" onClick={() => navigate('/committee')}>
          운영진 및 자문위원 페이지 <ArrowRightOutlined />
        </MoreLink>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  ${revealStyle}
  padding: 120px 0;
  background: #fff;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  max-width: 720px;
  width: 100%;
  margin: 0 auto 64px;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #f8fafc;

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 24px 12px;

    & + div {
      border-left: 1px solid #e8edf3;
    }
  }

  strong {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.1;
    color: ${MainColor};
  }

  span {
    font-size: 0.9rem;
    color: #6b7482;
  }
`;

const Group = styled.div`
  margin-bottom: 48px;
`;

const GroupTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e8edf3;

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: #111;
    margin: 0;
  }

  span {
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${SubColor};
  }

  em {
    margin-left: auto;
    font-style: normal;
    font-size: 0.9rem;
    color: #8a93a1;
  }
`;

const MemberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const MemberCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #edf1f6;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${SubColor}55;
    box-shadow: 0 10px 24px rgba(23, 55, 95, 0.08);
    transform: translateY(-2px);

    .avatar {
      background: ${SubColor};
      color: #fff;
    }
  }

  .avatar {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-weight: 700;
    color: ${SubColor};
    background: ${SubColor}14;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  strong {
    font-size: 1rem;
    color: #111;
  }

  span {
    font-size: 0.85rem;
    color: #6b7482;
    word-break: keep-all;
  }

  /* 좁은 화면에서는 두 줄 배치를 위해 이니셜을 숨긴다 */
  @media (max-width: 480px) {
    padding: 12px 14px;

    .avatar {
      display: none;
    }

    span {
      font-size: 0.8rem;
    }
  }
`;

const TeamRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled.div`
  padding: 32px;
  border-radius: 16px;
  color: #fff;
  background:
    radial-gradient(circle at 100% 0%, rgba(143, 182, 227, 0.35), transparent 50%),
    linear-gradient(135deg, ${MainColor}, ${SubColor});

  .english {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #b9d3f0;
  }

  h3 {
    font-size: 1.15rem;
    font-weight: 700;
    color: #fff;
    margin: 8px 0 20px;
    word-break: keep-all;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  li {
    display: flex;
    flex-direction: column;
    padding: 12px 14px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.1);
  }

  strong {
    font-size: 1rem;
  }

  span {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const MoreLink = styled.button`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 48px;
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid #d5deea;
  background: #fff;
  color: #4a5568;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${SubColor};
    color: ${SubColor};
  }
`;

export default CommitteeSection;
